import instance from '../../api';
import { message } from 'antd';
import { ErrorNotification } from '../../../Utils/data';
import { notification } from 'antd';
import { AxiosError } from 'axios';

// types

export type FreeTrialSortBy = 'name' | 'quantity' | 'timeLeft';

export interface ApiKeysListParams {
  companyId: any;
  page?: number | string;
  perPage?: number | string;
  isActive?: boolean;
  search?: string;
}

export interface UpdateApiKeyStatusBody {
  id: number | string;
  companyId: number | string;
  is_active: boolean;
}

export interface IftaWithJumpBody {
  companyId: number;
  year: number;
  quarter?: number;
  month?: number;
  states: string[];
  vehicles: number[];
  page: number;
  perPage: number;
}

export const companies = {
  async read(
    companyName: string = '',
    usdotId: string | number = '',
    sort?: boolean,
    skip: string = '1',
    isDisabled: string = '',
    ownerId: string = '',
    isDispute: string = '',
    uid: string = '',
    isActive?: boolean,
    stripeCustomerId: string | number = '',
    publicId: string | number = '',
    isContract?: boolean,
  ) {
    if (typeof companyName === 'string') {
      companyName = companyName.trim();
    }

    const hasCompanySearch = Boolean(companyName);

    const params = new URLSearchParams();

    if (companyName) params.append('searchName', companyName);
    if (usdotId) params.append('searchUsdot', String(usdotId));
    if (uid) params.append('uid', uid);

    if (isDisabled) {
      params.append('isDisable', 'true');
      if (ownerId) params.append('ownerId', ownerId);
    } else if (isDispute) {
      params.append('isDispute', isDispute);
    }

    if (typeof isActive === 'boolean') {
      params.append('isActive', String(isActive));
    }

    if (stripeCustomerId) {
      params.append('stripeCustomerId', String(stripeCustomerId));
    }
    if (publicId) {
      params.append('publicId', String(publicId));
    }
    if (typeof isContract === 'boolean') {
      params.append('isContract', String(isContract));
    }

    // if (hasCompanySearch) {
    //   params.append('page', skip);
    //   params.append('perPage', '10');
    // }

    params.append('page', skip);
    params.append('perPage', '10');

    const url = `companies/list?${params.toString()}`;

    const { data }: any = await instance(url);

    return {
      data: data.data,
      count: data.count,
    };
  },
  async updateFreeTrial(freeTrialId: number | string, quantity: number) {
    message.loading({ content: 'Loading...', key: freeTrialId });
    const { data }: any = await instance(`stripes/free-trial/${freeTrialId}`, {
      method: 'POST',
      data: { quantity },
    }).then((u) => {
      setTimeout(() => {
        message.success({ content: 'Loaded!', key: freeTrialId, duration: 2 });
      }, 1000);
      return u;
    });
    return data;
  },

  // Moves the end date of an active free trial (same endpoint that opens it / updates quantity)
  async updateFreeTrialEndDate(companyId: number | string, endDate: string) {
    try {
      const { data }: any = await instance(`stripes/free-trial/${companyId}`, {
        method: 'POST',
        data: { endDate },
      });
      message.success('Free trial end date updated');
      return data;
    } catch (error: any) {
      if (error.response?.status) {
        ErrorNotification({
          status: error.response.status,
          message: error.response.data?.error?.message,
        });
      }
      throw error;
    }
  },

  async freeTrialList(
    searchName: string = '',
    usdot: string = '',
    isActive: string = '',
    skip: string = '1',
    sortBy?: FreeTrialSortBy,
    sortOrder?: 'asc' | 'desc',
  ) {
    const params = new URLSearchParams();
    if (searchName) params.append('searchName', searchName.trim());
    if (usdot) params.append('searchUsdot', usdot.trim());
    if (isActive !== '') params.append('is_active', isActive);
    params.append('page', skip);
    params.append('perPage', '10');
    // Without sortBy the backend keeps its default order (newest free trial first)
    if (sortBy) {
      params.append('sortBy', sortBy);
      params.append('sortOrder', sortOrder ?? 'asc');
    }

    const { data }: any = await instance(`companies/free-trial/list?${params.toString()}`);
    return { data: data.data, count: data.count };
  },

  // Change log of a single free trial (create / end date / quantity / note), newest first
  async freeTrialChanges(
    companyId: string | number,
    freeTrialId: string | number,
    page: number = 1,
    perPage: number = 10,
  ) {
    const params = new URLSearchParams();
    params.append('companyId', String(companyId));
    params.append('freeTrialId', String(freeTrialId));
    params.append('page', String(page));
    params.append('perPage', String(perPage));

    const { data }: any = await instance(
      `activeDeactives/free-trial-histories?${params.toString()}`,
    );
    return { data: data.data, count: data.count };
  },

  async freeTrialHistory(companyId: string | number, page: number = 1, perPage: number = 10) {
    const params = new URLSearchParams();
    params.append('companyId', String(companyId));
    params.append('page', String(page));
    params.append('perPage', String(perPage));

    const { data }: any = await instance(`companies/free-trial/history?${params.toString()}`);
    return { data: data.data, count: data.count };
  },

  async expires(
    companName: number | string,
    usdot: string,
    status: string,
    uid: string,
    skip: string | undefined,
  ) {
    try {
      if (typeof companName === 'string') {
        companName = companName.trim();
      }
      let url = `/companies/near-subscription-charge?page=${skip}&perPage=10&searchName=${companName}&searchUsdot=${usdot}&uid=${uid}&isActive=${status}`;
      const { data }: any = await instance(url);
      return {
        data: data?.companyInfo,
        count: data.count,
      };
    } catch (error: any) {
      if (error.response && error.response.status) {
        ErrorNotification({
          status: error.response.status,
          message: error.response.data.error.message,
        });
      }
    }
  },
  async companyNewsubscription(
    companName: number | string,
    usdot: string,
    status: string,
    uid: string,
    skip: string | undefined,
  ) {
    try {
      if (typeof companName === 'string') {
        companName = companName.trim();
      }
      let url = `/companies/new-subscription?page=${skip}&perPage=10&searchName=${companName}&searchUsdot=${usdot}&uid=${uid}&status=${status}`;
      const { data }: any = await instance(url);
      return {
        data: data?.companyInfo,
        count: data.count,
      };
    } catch (error: any) {
      if (error.response && error.response.status) {
        ErrorNotification({
          status: error.response.status,
          message: error.response.data.error.message,
        });
      }
    }
  },
  async companyData(companyId: string | number | undefined) {
    if (companyId) {
      // await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 1000));
      const { data }: { data: any } = await instance(`companies/find/${companyId}`);
      return data;
    }
  },
  // The company's current ELD type straight from the server. Tabs that send config.eldType
  // with a save use this instead of the value loaded when the tab opened, so a save can't
  // undo a legal / non-legal migration that happened in the meantime.
  async latestEldType(companyId: string | number | undefined): Promise<string | null> {
    const company = await companies.companyData(companyId);
    return company?.config?.eldType || null;
  },
  async companyPatchData(companyData: any, id: number | string) {
    try {
      const key = 'updatable';
      message.loading({ content: 'Loading...', key });
      const { data }: { data: any } = await instance(`companies/edit-company/${id}`, {
        method: 'PATCH',
        data: companyData,
      }).then((u) => {
        setTimeout(() => {
          message.success({ content: 'Loaded!', key, duration: 2 });
        }, 1000);
        return u;
      });
      return data;
    } catch (error: any) {
      if (error.response && error.response.status) {
        ErrorNotification({
          status: error.response.status,
          message: error.response.data.error.message,
        });
      }
    }
  },
  async companyPatchWhichservice(companyId: any) {
    try {
      const key = 'updatable';
      message.loading({ content: 'Loading...', key });
      const { data }: { data: any } = await instance(`companies/change-whichservice/${companyId}`, {
        method: 'PATCH',
        data: companyId,
      }).then((u) => {
        setTimeout(() => {
          message.success({ content: 'Loaded!', key, duration: 2 });
        }, 1000);
        return u;
      });
      return data;
    } catch (error: any) {
      if (error.response && error.response.status) {
        ErrorNotification({
          status: error.response.status,
          message: error.response.data.error.message,
        });
      }
    }
  },
  async stripeMoves(body: any) {
    const key = 'updatable';
    message.loading({ content: 'Loading...', key });
    try {
      const { data }: { data: any } = await instance(`stripes/stripe-moves`, {
        method: 'POST',
        data: body,
      }).then((u) => {
        setTimeout(() => {
          message.success({ content: 'Loaded!', key, duration: 2 });
        }, 1000);
        return u;
      });
      return data;
    } catch (error: any) {
      if (error.response && error.response.status) {
        ErrorNotification({
          status: error.response.status,
          message: error.response.data.error.message,
        });
      }
    }
  },
  async vehicleData(vehicleId: string | number | undefined) {
    if (vehicleId) {
      const { data }: { data: any } = await instance(`vehicles/${vehicleId}`);
      return data;
    }
  },
  async searchVehicleData(truck_number?: string | number, companyId?: string | number) {
    const params = new URLSearchParams();
    params.append('perPage', '10000');

    if (companyId) params.append('companyId', String(companyId));
    if (truck_number) params.append('truckSearch', String(truck_number));

    const { data }: any = await instance(`vehicles/searching-list?${params.toString()}`);

    return {
      data: data.data,
      count: data.count,
    };
  },
  async subscription(stripeId: string, companyId: string) {
    try {
      if (stripeId) {
        const { data }: { data: any } = await instance(
          `stripes/admin/retrieve-subscription-information?subscriptionId=${stripeId}&companyId=${companyId}`,
        );
        return { data: data };
      }
    } catch (error: any) {
      if (error.response && error.response.status) {
        ErrorNotification({
          status: error.response.status,
          message: error.response.data.error.message,
        });
      }
    }
  },
  async updateSubscriptionData(body: number | string | object) {
    try {
      const key = 'updatable';
      message.loading({ content: 'Loading...', key });
      const { data }: { data: any } = await instance(`stripes/admin/update-subsciption`, {
        method: 'POST',
        data: body,
      }).then((u) => {
        setTimeout(() => {
          message.success({ content: 'Loaded!', key, duration: 2 });
        }, 1000);
        return u;
      });
      return data;
    } catch (error: any) {
      if (error.response && error.response.status) {
        ErrorNotification({
          status: error.response.status,
          message: error.response.data.error.message,
        });
      }
    }
  },
  async getPrice(companyId: string | number | undefined, productId?: string) {
    if (companyId) {
      // await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 1000));
      const params = new URLSearchParams({ companyId: String(companyId) });
      if (productId) {
        params.append('productId', productId);
      }
      const { data }: { data: any } = await instance(`stripes/admin/prices?${params.toString()}`);
      return data;
    }
  },
  async getProducts(companyId: string | number | undefined) {
    if (companyId) {
      const { data }: { data: any } = await instance(
        `stripes/admin/products?companyId=${companyId}`,
      );
      return data;
    }
  },
  async getHistorySubscription(status: string, next: object | any, companyId: string) {
    try {
      const { data } = await instance(
        `stripes/admin/getinvoiceview/?status=${status}&limit=10&companyId=${companyId}&next=${next?.value}&lastInvoiceId=${next.lastInvoiceId}`,
      );
      return data;
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.status) {
        ErrorNotification({
          status: error.response.status,
          message: error.response.data?.error?.message || 'Unknown error',
        });
      }
    }
  },
  async renewInvoice(body: { invoiceId: string; companyId: string }) {
    try {
      const { data }: { data: any } = await instance(`stripes/admin/renew-invoice`, {
        method: 'POST',
        data: body,
      });
      notification.success({
        message: 'Renew Invoice',
        description: data?.message || 'Invoice renewed successfully',
        placement: 'topRight',
      });
      return data;
    } catch (error: any) {
      if (error.response && error.response.status) {
        ErrorNotification({
          status: error.response.status,
          message: error.response.data.error.message,
        });
      }
    }
  },
  async checkInvoices(getCompanyId: string | number | undefined) {
    if (getCompanyId) {
      try {
        const { data }: { data: any } = await instance(
          `/stripes/check-invoices?companyId=${getCompanyId}`,
        );
        notification.info({
          message: 'Check Invoices!',
          description: data.message,
          placement: 'topRight',
        });
        return data;
      } catch (error: any) {
        if (error.response && error.response.status) {
          ErrorNotification({
            status: error.response.status,
            message: error.response.data.error.message,
          });
        }
      }
    }
  },
  async regenerate(body: number | string | object) {
    try {
      const key = 'updatable';
      message.loading({ content: 'Loading...', key });
      const { data }: { data: any } = await instance(`/iftaReports/admin-regenerate`, {
        method: 'POST',
        data: body,
      }).then((u) => {
        setTimeout(() => {
          message.success({ content: 'Loaded!', key, duration: 2 });
        }, 1000);
        return u;
      });
      return data;
    } catch (error: any) {
      if (error.response && error.response.status) {
        ErrorNotification({
          status: error.response.status,
          message: error.response.data.error.message,
        });
      }
    }
  },

  async iftaWithJumpGetStatesAll(
    companyId: number | string | undefined,
    body: IftaWithJumpBody,
  ) {
    try {
      const { data }: { data: any } = await instance(`/iftaReports/admin/v2/getStatesAll`, {
        method: 'POST',
        data: body,
        headers: companyId ? { companyId } : undefined,
      });
      return data;
    } catch (error: any) {
      if (error.response && error.response.status) {
        ErrorNotification({
          status: error.response.status,
          message: error.response.data.error.message,
        });
      }
    }
  },

  async iftaWithJumpAll(companyId: number | string | undefined, body: IftaWithJumpBody) {
    try {
      const { data }: { data: any } = await instance(`/iftaReports/admin/v2/all`, {
        method: 'POST',
        data: body,
        headers: companyId ? { companyId } : undefined,
      });
      return data;
    } catch (error: any) {
      if (error.response && error.response.status) {
        ErrorNotification({
          status: error.response.status,
          message: error.response.data.error.message,
        });
      }
    }
  },

  async userMe(companyId: string | number | undefined) {
    if (companyId) {
      const { data }: { data: any } = await instance(`users/me`);
      return data;
    }
  },

  async getStripeLogs(uid: string) {
    try {
      const key = 'updatable';
      message.loading({ content: 'Loading...', key });
      const { data }: { data: any } = await instance(`download-company-log/${uid}`).then((u) => {
        setTimeout(() => {
          message.success({ content: 'Loaded!', key, duration: 2 });
        }, 1000);
        return u;
      });
      return data;
    } catch (error: any) {
      if (error.response && error.response.status) {
        ErrorNotification({
          status: error.response.status,
          message: error.response.data.error.message,
        });
      }
    }
  },
  async getStripListType() {
    try {
      const { data }: { data: any } = await instance(`/stripes/list-stripe-types`).then((u) => {
        return u;
      });
      return data;
    } catch (error: any) {
      if (error.response && error.response.status) {
        ErrorNotification({
          status: error.response.status,
          message: error.response.data.error.message,
        });
      }
    }
  },

  async getEventNames() {
    try {
      const { data }: { data: any } = await instance(`/alerts/admin/event-names`).then((res) => {
        return res;
      });
      return data;
    } catch (error: any) {
      if (error.response && error.response.status) {
        ErrorNotification({
          status: error.response.status,
          message: error.response.data.error.message,
        });
      }
    }
  },

  async companyPatchDataAlert(
    companyData: {
      cameraAlertChatId?: string | null;
      cameraAlertEmail?: string | null;
      telegramChatId?: string | null;
      settings?: Record<string, boolean>;
    },
    companyId: number | string,
  ) {
    try {
      const key = 'updatable';
      message.loading({ content: 'Loading...', key });

      const { data } = await instance.patch(`/companies/admin/edit/${companyId}`, companyData);

      setTimeout(() => {
        message.success({ content: 'Updated successfully!', key, duration: 2 });
      }, 1000);

      return data;
    } catch (error: any) {
      if (error.response && error.response.status) {
        ErrorNotification({
          status: error.response.status,
          message: error.response.data.error.message,
        });
      }
    }
  },
  async companyMultipleEdit(dataBody: any) {
    try {
      const { data } = await instance.patch('/companies/admin/multiple-edit', dataBody);
      return data;
    } catch (error: any) {
      if (error.response && error.response.status) {
        ErrorNotification({
          status: error.response.status,
          message: error.response.data.error.message,
        });
      }
    }
  },

  async sendTelegramTestMessage(dataBody: { chatId: string; botType: string; message: string }) {
    try {
      const { data } = await instance.post('/admins/telegram/send-test-message', dataBody);

      return data;
    } catch (error: any) {
      if (error.response && error.response.status) {
        ErrorNotification({
          status: error.response.status,
          message: error.response.data?.error?.message,
        });
      }
    }
  },
  async sendIftaReport(baseURL: string, body: any): Promise<any> {
    return fetch(`${baseURL}/iftaReports/admin/file-range`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('token') || '',
      },
      body: JSON.stringify(body),
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to generate IFTA report');
        }

        return Promise.all([res.blob(), res.headers.get('content-disposition')]);
      })
      .then(([blob, contentDisposition]) => {
        if (!blob || !blob.size) {
          return {
            file: 'There is no information between the selected date.',
            fileName: null,
          };
        }

        const fileName = contentDisposition?.match(/filename="?(.+?)"?$/)?.[1] ?? 'ifta-report.pdf';

        const file = new File([blob], fileName, {
          type: blob.type || 'application/pdf',
          lastModified: Date.now(),
        });

        return {
          file,
          fileName,
        };
      });
  },

  // X- API -KEYS

  async getApiKeysList(params: ApiKeysListParams) {
    try {
      const { data } = await instance('/admins/api-keys-list', {
        params: {
          companyId: params.companyId,
          page: params.page,
          perPage: params.perPage,
          isActive: params.isActive,
          search: params.search,
        },
      });
      return data;
    } catch (error: any) {
      if (error.response && error.response.status) {
        ErrorNotification({
          status: error.response.status,
          message: error.response.data?.error?.message || error.message,
        });
      }
    }
  },

  async updateApiKeyStatus(body: UpdateApiKeyStatusBody) {
    try {
      const { data } = await instance(`/admins/api-key-status/${body.id}`, {
        method: 'PATCH',
        data: {
          is_active: body.is_active,
        },
        headers: {
          companyId: body.companyId,
        },
      });
      return data;
    } catch (error: any) {
      if (error.response && error.response.status) {
        ErrorNotification({
          status: error.response.status,
          message: error.response.data?.error?.message || error.message,
        });
      }
    }
  },
};
